/**
 * use-active-location — PWA location context hook (JQ4-12 WU-23).
 *
 * Fetches the caller's assigned locations on mount, validates the localStorage
 * cache, and exposes the current state to the boot gate in _authenticated.tsx.
 *
 * Mid-session listener (JQ4-12 W-1):
 * Registers a window listener for `jq4race:location-access-denied` so that a
 * 403 LOCATION_ACCESS_DENIED / LOCATION_INACTIVE mid-session re-triggers the
 * boot resolution without requiring a page refresh.
 */

import { getMyLocations, LOCATION_DENIED_EVENT } from '@jq4race/api-client';
import type { AssignedLocationResponse, OfflineProfile } from '@jq4race/shared';
import { useCallback, useEffect, useState } from 'react';

import { getCurrentSession } from '../../../app/auth-state';
import { OPERATOR_ROLE } from '../../auth/domain/entities/session';
import {
  readOfflineProfile,
  saveOfflineProfile,
} from '../../offline/application/offline-profile-store';
import { isOfflineProfileValid } from '../../offline/domain/offline-profile';
import { offlineDb } from '../../offline/infrastructure/dexie/offline-db';
import { DexieKvStore } from '../../offline/infrastructure/dexie-kv';
import { type BootLocationResult, resolveBootLocation } from './resolve-boot-location';

const ACTIVE_LOCATION_KEY = 'jq4race:active_location_id';

// Shared KvStore instance — cheap wrapper around the single `offlineDb`
// connection, no reason for every render/call to construct its own.
const offlineKv = new DexieKvStore(offlineDb);

function activeLocationIdFor(result: BootLocationResult): string | null {
  return result.action === 'auto-select' || result.action === 'proceed' ? result.locationId : null;
}

/**
 * Persists the offline profile (design D11) after a SUCCESSFUL online boot
 * fetch — the first point in the app where both the authenticated operator
 * and their location list are known together. Fire-and-forget: a Dexie
 * write failure (e.g. private-browsing storage restrictions) must never
 * block or fail the online boot path.
 */
function persistOfflineProfileFireAndForget(
  locations: AssignedLocationResponse[],
  result: BootLocationResult,
): void {
  const session = getCurrentSession();
  if (!session || session.user.role !== OPERATOR_ROLE || !session.user.organizationId) return;
  const profile: OfflineProfile = {
    userId: session.user.id,
    orgId: session.user.organizationId,
    role: OPERATOR_ROLE,
    email: session.user.email,
    displayName: session.user.email ?? session.user.id,
    locations,
    activeLocationId: activeLocationIdFor(result),
    savedAt: new Date().toISOString(),
  };
  void saveOfflineProfile(offlineKv, profile).catch(() => {
    // Best-effort persistence — see comment above.
  });
}

export interface ActiveLocationState {
  isLoading: boolean;
  bootResult: BootLocationResult | null;
  locations: AssignedLocationResponse[];
  activeLocationId: string | null;
  /** Call after the user selects a location from the picker. */
  selectLocation: (id: string) => void;
}

export function useActiveLocation(): ActiveLocationState {
  const [isLoading, setIsLoading] = useState(true);
  const [locations, setLocations] = useState<AssignedLocationResponse[]>([]);
  const [bootResult, setBootResult] = useState<BootLocationResult | null>(null);
  const [activeLocationId, setActiveLocationId] = useState<string | null>(
    typeof localStorage !== 'undefined' ? localStorage.getItem(ACTIVE_LOCATION_KEY) : null,
  );

  /**
   * Shared fetch-and-resolve path.
   * Used both on mount and when the mid-session denied event fires.
   * `cancelled` ref prevents state updates on a stale closure.
   */
  const runLoad = useCallback(async (signal: { cancelled: boolean }) => {
    try {
      const res = await getMyLocations();
      if (signal.cancelled) return;

      const cachedId =
        typeof localStorage !== 'undefined' ? localStorage.getItem(ACTIVE_LOCATION_KEY) : null;

      const result = resolveBootLocation(res.locations, cachedId);

      // Side-effect: persist selection / clear stale cache before rendering.
      if (result.action === 'auto-select') {
        localStorage.setItem(ACTIVE_LOCATION_KEY, result.locationId);
        setActiveLocationId(result.locationId);
      } else if (result.action === 'show-picker') {
        localStorage.removeItem(ACTIVE_LOCATION_KEY);
        setActiveLocationId(null);
      }

      setLocations(res.locations);
      setBootResult(result);
      persistOfflineProfileFireAndForget(res.locations, result);
    } catch {
      if (signal.cancelled) return;

      // Offline fallback (task 3.6 / spec "Offline reopen after restart"):
      // a request failure while navigator.onLine is false is very likely
      // the device being offline, not a real backend error — fall back to
      // the last-saved offline profile's locations instead of blocking the
      // operator from even opening the shell. Online failures (onLine still
      // true) keep the original "block" behavior — a real backend/auth
      // problem should not be silently masked by a stale offline profile.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        const profile = await readOfflineProfile(offlineKv).catch(() => null);
        if (profile && isOfflineProfileValid(profile, new Date())) {
          const cachedId =
            typeof localStorage !== 'undefined' ? localStorage.getItem(ACTIVE_LOCATION_KEY) : null;
          const fallbackResult = resolveBootLocation(
            profile.locations,
            cachedId ?? profile.activeLocationId,
          );
          if (!signal.cancelled) {
            setLocations(profile.locations);
            setBootResult(fallbackResult);
            const fallbackActiveId = activeLocationIdFor(fallbackResult);
            if (fallbackActiveId) setActiveLocationId(fallbackActiveId);
          }
          return;
        }
      }

      // Network failure with no usable offline fallback: treat as "block" —
      // operator cannot proceed without a known location context. A retry /
      // logout is the correct UX path.
      if (!signal.cancelled) {
        setBootResult({ action: 'block' });
      }
    } finally {
      if (!signal.cancelled) setIsLoading(false);
    }
  }, []);

  // Boot load on mount.
  useEffect(() => {
    const signal = { cancelled: false };
    void runLoad(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [runLoad]);

  // Mid-session listener: re-run boot resolution when the 403 interceptor
  // fires `jq4race:location-access-denied` (localStorage already cleared by
  // the interceptor at that point).
  useEffect(() => {
    function handleDenied() {
      // Clear local activeLocationId state to stay in sync with the
      // interceptor's localStorage removal.
      setActiveLocationId(null);
      // Re-run the full fetch-and-resolve path (idempotent, safe to call
      // multiple times — each call is independent with its own signal).
      const signal = { cancelled: false };
      void runLoad(signal);
    }

    window.addEventListener(LOCATION_DENIED_EVENT, handleDenied);
    return () => {
      window.removeEventListener(LOCATION_DENIED_EVENT, handleDenied);
    };
  }, [runLoad]);

  function selectLocation(id: string) {
    localStorage.setItem(ACTIVE_LOCATION_KEY, id);
    setActiveLocationId(id);
    setBootResult({ action: 'proceed', locationId: id });
  }

  return { isLoading, bootResult, locations, activeLocationId, selectLocation };
}
